<template>
    <section>
        <div class="text-center card-details--container py-5">
            <img class="card-details--image" src="@/assets/images/decoration/icons/Padlock.jpg" alt="secure payments image">
            <p class="card-details--text" v-text="$t('CARD_DETAILS_FORM_AND_LAST')"/>
        </div>

        <div v-if="isCanvasser && isUSA" class="radio-container">
            <radio-field
                v-for="(item, index) in options"
                :key="index"
                v-model="val"
                :option="item.option"
                :label="item.label"/>
        </div>

        <div v-if="skipCard === false">
            <!-- Stripe Terminal from -->
            <stripe-terminal-form v-if="showTerminal && isUSA"/>

            <!-- Stripe Payment Request Button -->
            <stripe-payment-request-button v-if="showPaymentRequestButton" :setupIntent="setupIntent" @register="registerCustomer"/>

            <!--Stripe Elements form-->
            <stripe-elements-form
                v-if="showStripeElementForm && stripeMounted"
                ref="cardForm"
                :full-name="fullName"
                :billing-address="deliveryAddressIsBilling ? deliveryAddress : billingAddress"
                :has-been-requested="userHasRequesed"/>

            <div v-if="!stripeMounted" class="radio-container retry">
                <p class="card-details--text" v-text="$t('TRY_AGAIN')"/>
                <i class="loader__loading-icon fa-solid fa-arrows-rotate" @click="mountStripe"/>
            </div>

            <!--Billing address form-->
            <div>
                <checkbox
                    :initialValue = "deliveryAddressIsBilling"
                    v-model="deliveryAddressIsBilling">
                    {{ $t('BILLING_ADDRESS_FORM_BILLING_ADDRESS') }}
                </checkbox>

                <div :class="billingAddressContainerClass" :style="billingAddressContainerStyle">
                    <billing-address-form
                        :error-handler="errorHandler"
                        v-model="billingAddress"/>
                </div>
            </div>
        </div>

        <div v-if="allowSkipCard">
            <checkbox
                v-model="skipCard">
                {{ $t('CARD_DETAILS_FORM_I_WILL_ENTER') }}
            </checkbox>
        </div>

        <!--Error list-->
        <div class="text-center">
            <p class="error" v-if="errorHandler.hasErrorMessage()" v-text="errorHandler.getErrorMessage()"></p>
            <p class="error my-2" v-text="error" v-if="error"></p>
        </div>
        <div class="w-full p-2"
             v-if="errorHandler.getAllErrors()">
            <ul class="error text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"/>
                    <span class="font-normal" v-text="$t(errorMsg)"/>
                </li>
            </ul>
        </div>
        <supported-cards />
        <order-summary />

        <div class="py-1 px-5">
            <p class="input__message" v-if="isUSA" v-html="$t('MARKETING_PREFERENCES', {price})"></p>
        </div>

        <!-- Form button -->
        <div v-if="showSignUp" class="text-center">
            <submit-button
                :processing="processing"
                color="green"
                @click="submitCurrentStep()">
                {{ $t('CARD_DETAILS_FORM_SIGN_ME_UP') }}
            </submit-button>
        </div>
    </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { SECTION_PAYMENT } from '@/constants/Registration';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import Checkbox from '@/components/forms/fields/Checkbox';
import RegistrationService from '@/services/users/RegistrationService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import BillingAddressForm from '@/components/forms/addresses/BillingAddressForm';
import OrderSummary from '@/components/forms/fields/OrderSummary';
import SupportedCards from '@/components/forms/fields/SupportedCards';
import RadioField from '@/components/forms/fields/RadioField';
import StripeElementsForm from '@/components/forms/cardDetails/StripeElementsForm';
import StripeTerminalForm from '../cardDetails/StripeTerminalForm';
import StripePaymentRequestButton from '../cardDetails/StripePaymentRequestButton';

export default {
    name: 'CardDetailsForm',
    data() {
        return {
            processing: false,
            skipCard: false,
            showCouponCode: false,
            userHasRequesed: false,
            billingAddressOverflowVisible: false,
            errorHandler: new ErrorResponseHandler(),
            price: process.env.VUE_APP_BOX_PRICE,
            options: [
                {
                    option: 'terminal',
                    label: this.$t('READER_OPTION'),
                },
                {
                    option: 'card',
                    label: this.$t('CARD_OPTION'),
                },
            ],
            val: 'terminal',
            error: null,
        };
    },
    props: {
        allowSkipCard: {
            default: false,
            type: Boolean,
        },
        enableCouponField: {
            default: false,
            type: Boolean,
        },
    },
    mounted() {
        this.is3DSEnabled && this.fetchStripeSetupIntent({ email: this.email, gateway: this.paymentGateway });
        this.showCouponCode = (this.friendCode || '').length > 0;
    },
    methods: {
        ...mapActions({
            fetchStripeSetupIntent: 'account/fetchStripeSetupIntent',
            mountStripe: 'cookies/mountStripe',
        }),

        // Handle validating and submitting this step.
        submitCurrentStep() {
            this.userHasRequesed = true;
            this.error = null;

            this.processing = true;

            if (this.skipCard === false) {
                if (this.is3DSEnabled) {
                    this.$refs.cardForm.handleScaCardSubmission()
                        .then(tokenData => {
                            let payload = {
                                payment_token: tokenData.setupIntentId,
                                stripe_card_id: tokenData.setupIntentId,
                                payment_method: 'card',
                                gateway: this.paymentGateway,
                            };
                            this.isCanvasser
                                ? this.registerCanvassedCustomer(payload)
                                : this.registerCustomer(payload);
                        })
                        .catch((err) => {
                            this.error = err;
                            this.handleFail();
                        });
                } else {
                    this.$refs.cardForm.handleFetchingToken()
                        .then(tokenData => {
                            let payload = {
                                payment_token: tokenData.tokenId,
                                stripe_card_id: tokenData.cardId,
                                payment_method: 'card',
                                gateway: this.paymentGateway,
                            };
                            this.isCanvasser
                                ? this.registerCanvassedCustomer(payload)
                                : this.registerCustomer(payload);
                        })
                        .catch((err) => {
                            this.error = err;
                            this.handleFail();
                        });
                }
            } else {
                // Only canvasser can get here.
                let payload = {
                    payment_token: null,
                    stripe_card_id: null,
                    payment_method: 'card',
                    gateway: this.paymentGateway,
                };
                this.registerCanvassedCustomer(payload);
            }
        },
        // Continue to register the customer with our API.
        registerCustomer(payload) {
            RegistrationService.registerCustomer(payload)
                .then(() => {
                    this.$store.commit('register/setSectionComplete', SECTION_PAYMENT);
                    this.$emit('registered');
                })
                .catch(error => {
                    this.handleFail();
                    this.$store.commit('account/setSetupIntent', null);
                    this.fetchStripeSetupIntent({ email: this.email, gateway: this.paymentGateway });
                    const hasAmex = error.response?.data?.errors.error[0].includes('Amex');
                    if (hasAmex) return this.errorHandler.setResponse({ error: this.$t('AMERICAN_EXPRESS_NOT_SUPPORTED') });
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                });
        },
        // Continue to register the canvassed customer with our API.
        registerCanvassedCustomer(payload) {
            RegistrationService.registerCanvassedCustomer(payload)
                .then(() => {
                    this.$store.commit('register/setSectionComplete', SECTION_PAYMENT);
                    this.$emit('registered');
                })
                .catch(error => {
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                    this.handleFail();
                });
        },
        // Handle failed sign up or card token request.
        handleFail() {
            this.processing = false;
            this.$scrollTo(`.input--invalid`, {
                offset: -100,
            });
        },
    },
    computed: {
        ...mapGetters({
            fullName: 'register/getFullName',
            email: 'register/getEmail',
            isUSA: 'localisation/isUSA',
            isDEU: 'localisation/isDEU',
            isCanvasser: 'auth/isCanvasser',
            isUser: 'auth/isUser',
            setupIntent: 'account/getSetupIntent',
            is3DSEnabled: 'payment/is3DSEnabled',
            isfetchingSetupIntent: 'account/isfetchingSetupIntent',
            paymentGateway: 'payment/getStripeGateway',
            stripeMounted: 'cookies/isStripeMounted',
        }),

        billingAddress: {
            get() {
                return this.$store.getters['register/getBillingAddress'];
            },
            set(value) {
                this.$store.commit('register/setBillingAddress', value);
            },
        },

        deliveryAddress: {
            get() {
                return this.$store.getters['register/getDeliveryAddress'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddress', value);
            },
        },

        friendCode: {
            get() {
                return this.$store.getters['register/getFriendCode'];
            },
            set(code) {
                this.$store.commit('register/setFriendCode', code);
            },
        },

        deliveryAddressIsBilling: {
            get() {
                return this.$store.getters['register/getDeliveryAddressIsBilling'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddressIsBilling', value);
            },
        },

        billingAddressContainerClass() {
            return !this.deliveryAddressIsBilling ? 'accordion__md accordion__md--active' : 'accordion__md';
        },

        billingAddressContainerStyle() {
            return this.billingAddressOverflowVisible ? { overflow: 'visible' } : {};
        },
        showSignUp() {
            return (this.isCanvasser && this.val === 'card') || !this.isCanvasser || this.skipCard || (this.isCanvasser && this.isDEU);
        },

        showTerminal() {
            return this.isCanvasser && this.val === 'terminal';
        },

        showStripeElementForm() {
            return (this.isCanvasser && this.val === 'card') || !this.isCanvasser || (this.isCanvasser && this.isDEU);
        },

        showPaymentRequestButton() {
            return this.is3DSEnabled && !this.isfetchingSetupIntent && this.setupIntent && !this.isCanvasser;
        },
    },
    watch: {
        showCouponCode(value) {
            if (value === false) {
                this.friendCode = null;
            }
        },
        deliveryAddressIsBilling: {
            immediate: true,
            handler: function(value) {
                if (value === false) {
                    // Delay overflow visible while the expand transition is taking place
                    setTimeout(() => {
                        this.billingAddressOverflowVisible = true;
                    }, 500);
                } else {
                    this.billingAddressOverflowVisible = false;
                }
            },
        },
        val(value) {
            this.$emit('input', value);
        },
    },
    components: {
        Checkbox,
        SubmitButton,
        StripeElementsForm,
        StripePaymentRequestButton,
        BillingAddressForm,
        OrderSummary,
        SupportedCards,
        StripeTerminalForm,
        RadioField,
    },
};
</script>

<style scoped>
    .radio-container{
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 20px 20px;
    }

    .retry {
        flex-direction: column;
    }
</style>
